import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';

import { IGeneralConstants } from '@interfaces/general-constants';
import { IUser } from '@interfaces/users';
import { AuthService } from '@services/auth/auth.service';
import { FirebaseService } from '@services/firebase/firebase.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { environment } from 'src/environments/environment';

const BANNER_SOURCE = 'side_menu';
const WEB2_LOGIN_PATH = '/login';
const WEB2_EMAIL_PARAM = 'email';

@Injectable({
  providedIn: 'root',
})
export class Web2BannerService {
  readonly isVisible: Observable<boolean>;
  private readonly isWeb: boolean;
  private hasLoggedView: boolean;
  private userEmail: string;

  constructor(
    private authService: AuthService,
    private firebaseService: FirebaseService,
    private navigationService: NavigationService,
    private platform: Platform,
  ) {
    this.isWeb = !this.platform.is('cordova');
    this.hasLoggedView = false;
    this.isVisible = combineLatest([
      this.authService.currentUserSource,
      this.firebaseService.currentGeneralConstants,
    ]).pipe(
      tap(([user]: [IUser, IGeneralConstants]) => this.userEmail = user ? user.email : undefined),
      map(([user, generalConstants]: [IUser, IGeneralConstants]) => this.canShowBanner(user, generalConstants)),
      distinctUntilChanged(),
    );
  }

  logBannerView(): void {
    if (this.hasLoggedView) { return; }
    this.hasLoggedView = true;
    this.firebaseService.logEvent('show_web2_banner', { source: BANNER_SOURCE });
  }

  goToWeb2(): void {
    this.firebaseService.logEvent('clicked_web2_banner', { source: BANNER_SOURCE });
    this.navigationService.openInBrowser(this.getWeb2LoginUrl());
  }

  private getWeb2LoginUrl(): string {
    const loginUrl = `${environment.web2Url}${WEB2_LOGIN_PATH}`;
    if (!this.userEmail) { return loginUrl; }
    return `${loginUrl}?${WEB2_EMAIL_PARAM}=${encodeURIComponent(this.userEmail)}`;
  }

  private canShowBanner(user: IUser, generalConstants: IGeneralConstants): boolean {
    if (!this.isWeb || environment.isPartnerSite) { return false; }
    if (!user || !generalConstants || !generalConstants.web2Banner) { return false; }
    const config = generalConstants.web2Banner;
    if (!config.active) { return false; }
    if (config.showToAll) { return true; }
    return user.web2Banner === true;
  }
}
